import React, { useState } from "react";
import {
  Table,
  Tag,
  Switch,
  Typography,
  Dropdown,
  Menu,
  Button,
  Modal,
  message,
} from "antd";
import { EyeOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useUpdateTable } from "../../hooks/useTables";

const { Text } = Typography;

export interface TableData {
  id: number;
  location_id: number;
  number: string;
  is_booked: boolean;
  qrcode: string;
  slug: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  location: {
    id: number;
    name: string;
    address: string;
    code: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
}

interface TableComponentProps {
  data: TableData[];
  onViewQRCode: (table: TableData) => void;
  onDeleteTable: (tableId: number) => void;
  onBulkDeleteTables: (tableIds: number[]) => void;
  isLoading: boolean;
  canModifyTable: boolean;
  canDeleteTable: boolean;
}

const TableComponent: React.FC<TableComponentProps> = ({
  data,
  onViewQRCode,
  onDeleteTable,
  onBulkDeleteTables,
  isLoading,
  canModifyTable,
  canDeleteTable,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const { mutate: updateTable } = useUpdateTable();

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select tables to delete");
      return;
    }

    Modal.confirm({
      title: "Delete Multiple Tables",
      content: `Are you sure you want to delete ${selectedRowKeys.length} table(s)?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        onBulkDeleteTables(selectedRowKeys as number[]);
        setSelectedRowKeys([]);
      },
    });
  };

  const rowSelection = canDeleteTable
    ? {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
          setSelectedRowKeys(newSelectedRowKeys);
        },
        onSelectAll: (
          selected: boolean,
          selectedRows: TableData[],
          changeRows: TableData[]
        ) => {
          if (selected) {
            const allKeys = data.map((item) => item.id);
            setSelectedRowKeys(allKeys);
          } else {
            setSelectedRowKeys([]);
          }
        },
      }
    : undefined;

  const columns: ColumnsType<TableData> = [
    {
      title: "Availability",
      dataIndex: "is_booked",
      key: "availability",
      render: (is_booked, record) => (
        <Switch
          checked={!is_booked}
          disabled={!canModifyTable}
          onChange={() => {
            if (!canModifyTable) return;

            Modal.confirm({
              title: "Change Booked Status",
              content: `Are you sure you want to change the status for Table ${record.number}?`,
              okText: "Yes",
              cancelText: "No",
              onOk: () => {
                updateTable({
                  id: record.id,
                  location_id: record.location_id,
                  is_booked: !record.is_booked,
                });
              },
            });
          }}
        />
      ),
    },
    {
      title: "Table Number",
      dataIndex: "number",
      key: "number",
      render: (number) => <Text strong>{number}</Text>,
    },
    {
      title: "Date Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at) => dayjs(created_at).format("DD - MM - YYYY"),
    },
    {
      title: "Location",
      dataIndex: ["location", "name"],
      key: "location",
    },
    {
      title: "Booked Status",
      dataIndex: "is_booked",
      key: "booked_status",
      render: (is_booked) => (
        <Tag color={is_booked ? "red" : "green"}>
          {is_booked ? "Occupied" : "Free"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const menuItems = [];

        menuItems.push({
          key: "view",
          label: "View QR Code",
          icon: <EyeOutlined />,
          onClick: () => onViewQRCode(record),
        });

        if (canDeleteTable) {
          menuItems.push({
            key: "delete",
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => onDeleteTable(record.id),
          });
        }

        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown overlay={<Menu items={menuItems} />} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      {/* Bulk Actions Bar */}
      {canDeleteTable && selectedRowKeys.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
          <Text>{selectedRowKeys.length} table(s) selected</Text>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={handleBulkDelete}
          >
            Delete Selected ({selectedRowKeys.length})
          </Button>
        </div>
      )}

      <Table
        loading={isLoading}
        className="custom-table"
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={{ x: "max-content" }}
        rowKey="id"
        rowSelection={rowSelection}
      />
    </div>
  );
};

export default TableComponent;
